import { useMemo } from 'react';

import { Link as MuiLink } from '@mui/material';
import pluralize from 'pluralize';

import {
  Box,
  Column,
  EllipsisWithTooltip,
  Icons,
  LayoutCard,
  NoData,
  Skeleton,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  NoResults,
  Search,
  SearchableTable,
  SearchProvider,
  useSearchContext,
} from '@cast/shared-search-ui';
import type { BillingV1FeatureEntityUsage } from '@cast/types';

import { Link as RouterLink } from 'components/router';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { buildDboRoute, DboRoute } from 'features/db-optimization/v2/routes';

type DatabaseInstancesTableProps = {
  entities: BillingV1FeatureEntityUsage[];
  engineMap: Map<string, string>;
  colorMap: Map<string, string>;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  selectedRows: string[];
  onRowsSelected: (ids: string[]) => void;
  maxSelection?: number;
};

const getEntityKey = (entity: BillingV1FeatureEntityUsage): string =>
  entity.entityId ?? entity.entityName ?? '';

// ─── Title ───────────────────────────────────────────────────────────────────

const DatabaseInstancesTableTitle = () => {
  const searchCtx = useSearchContext();
  const count = searchCtx?.filteredData?.length ?? 0;

  if (searchCtx?.isLoading) {
    return <Skeleton variant="text" width={160} height={28} />;
  }

  return (
    <Typography
      variant="A1"
      color={tokens.theme.fg.structural.primary}
      data-testid="dbo-instances-records-count"
    >
      {count} {pluralize('database instance', count)}
    </Typography>
  );
};

// ─── Table ───────────────────────────────────────────────────────────────────

export const DatabaseInstancesTable = ({
  entities,
  engineMap,
  colorMap,
  isLoading,
  isError,
  refetch,
  selectedRows,
  onRowsSelected,
  maxSelection,
}: DatabaseInstancesTableProps) => {
  const sorted = useMemo(
    () => [...entities].sort((a, b) => (b.usage ?? 0) - (a.usage ?? 0)),
    [entities]
  );

  const handleRowsSelected = (ids: string[]) => {
    const limit = maxSelection ?? Infinity;
    const retained = selectedRows.filter((id) => ids.includes(id));
    const added = ids.filter((id) => !selectedRows.includes(id));
    const merged = [...retained, ...added].slice(0, limit);
    onRowsSelected(merged);
  };

  return (
    <SearchProvider<BillingV1FeatureEntityUsage>
      urlKey="dbo-usage-instances"
      data={sorted}
      isLoading={isLoading}
      freeTextPredicate={(item, text) =>
        !!(
          item.entityName?.toLowerCase().includes(text.toLowerCase()) ||
          item.entityId?.toLowerCase().includes(text.toLowerCase())
        )
      }
    >
      <LayoutCard
        title={<DatabaseInstancesTableTitle />}
        testId="dbo-instances-table-card"
        padding={false}
        slotProps={{
          header: { py: 12 },
          content: {
            gap: 12,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box px={16}>
          <Search placeholder="Search database instances" />
        </Box>

        <SearchableTable<BillingV1FeatureEntityUsage>
          cacheKey={tableCacheKeys.PLATFORM_USAGE.DATABASE_INSTANCES}
          testId="dbo-instances-table"
          rowKey={(item) => getEntityKey(item)}
          fillParentHeight
          hasFailed={isError}
          selectedRows={selectedRows}
          onRowsSelected={(ids) => handleRowsSelected(ids as string[])}
          components={{
            noData: <NoData entity="database instance" />,
            noResults: <NoResults entity="database instance" />,
            failed: <FailedToLoad refresh={refetch} />,
          }}
        >
          <Column<BillingV1FeatureEntityUsage>
            id="name"
            header="Name"
            minWidth={320}
            sortable
            accessor="entityName"
            renderCell={(item) => {
              const key = getEntityKey(item);
              const color = colorMap.get(key);
              const engine = item.entityId
                ? engineMap.get(item.entityId)
                : undefined;
              const dboLink = item.entityId
                ? buildDboRoute(DboRoute.SERVER_DASHBOARD, {
                    dbServerId: item.entityId,
                  })
                : undefined;

              return (
                <Stack direction="row" alignItems="center" gap={8}>
                  {/* Color bar */}
                  <Box
                    sx={{
                      width: 2,
                      height: 32,
                      borderRadius: '2px',
                      bgcolor: color ?? 'transparent',
                      flexShrink: 0,
                    }}
                  />

                  {/* Icon */}
                  <Icons.CastDatabaseOptimization size={16} />

                  {/* Name + engine */}
                  <Stack gap={2} sx={{ minWidth: 0 }}>
                    {dboLink ? (
                      <EllipsisWithTooltip
                        TypographyProps={{
                          variant: 'P12M',
                          color: tokens.theme.fg.selection.primary,
                        }}
                      >
                        <MuiLink
                          component={RouterLink}
                          to={dboLink}
                          underline="none"
                          sx={{
                            typography: 'P12M',
                            color: tokens.theme.fg.selection.primary,
                          }}
                          aria-label="Database provider"
                        >
                          {item.entityName ?? '–'}
                        </MuiLink>
                      </EllipsisWithTooltip>
                    ) : (
                      <EllipsisWithTooltip
                        TypographyProps={{ variant: 'P12R' }}
                      >
                        {item.entityName ?? '–'}
                      </EllipsisWithTooltip>
                    )}
                    {engine && (
                      <Typography
                        variant="P12R"
                        color={tokens.theme.fg.structural.secondary}
                      >
                        {engine}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              );
            }}
          />

          <Column<BillingV1FeatureEntityUsage>
            id="usage"
            header="Usage"
            width={180}
            sortable
            accessor="usage"
            headerCellWrapperSx={{ justifyContent: 'flex-end' }}
            cellWrapperSx={{ textAlign: 'right' }}
            renderCell={(item) => (
              <Typography
                color={tokens.theme.fg.structural.primary}
                variant="P12M"
              >
                {(item.usage ?? 0).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            )}
          />
        </SearchableTable>
      </LayoutCard>
    </SearchProvider>
  );
};
